import React, { useEffect, useRef, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";

/**
 * "Why Study <Program Name> at JRIIT?" benefits section.
 *
 * Used on the four main program pages. Receives the program name and an
 * array of benefit strings; styled to match the existing custom-pink + gold
 * palette used across the program pages. An optional `careerPaths` string
 * renders a subtle career-paths line below the bullets.
 */
const ProgramBenefits = ({ programName = "", benefits = [], careerPaths = "" }) => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 -right-10 w-72 h-72 bg-custom-pink/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 -left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div
          className={`text-center mb-10 md:mb-14 transform transition-all duration-700 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-custom-pink leading-tight">
            Why Study {programName} at JRIIT?
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto rounded-full mt-4" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {benefits.map((benefit, idx) => (
            <div
              key={idx}
              className={`group flex items-start gap-4 p-5 md:p-6 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-custom-pink/40 transition-all duration-300 transform ${
                visible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: `${150 + idx * 80}ms` }}
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-custom-pink/10 text-custom-pink flex items-center justify-center text-lg group-hover:bg-custom-pink group-hover:text-white transition-colors duration-300">
                <FaCheckCircle />
              </span>
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                {benefit}
              </p>
            </div>
          ))}
        </div>

        {careerPaths ? (
          <div
            className={`mt-10 md:mt-12 transform transition-all duration-700 ${
              visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
            }`}
            style={{ transitionDelay: `${150 + benefits.length * 80}ms` }}
          >
            <div className="rounded-2xl border border-custom-pink/20 bg-custom-pink/5 px-6 py-5 md:px-8 md:py-6 text-center">
              <p className="text-xs md:text-sm uppercase tracking-widest font-semibold text-custom-pink mb-2">
                Career Paths
              </p>
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                {careerPaths}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ProgramBenefits;
